import Cookies from 'js-cookie';
import { useContext, useEffect, useState } from 'react';
import constants from '../../../../constants/constants';
import { Button, DatePicker, Empty, Form, message, Select, Space, Spin, Tabs } from 'antd';
import Chart from 'react-google-charts';
import APIUtils from '../../../../api/APIUtils';
import WebLinks from '../../../../api/WebLinks';
import dayjs from 'dayjs';
import * as XLSX from "xlsx";
import { FileExcelOutlined } from "@ant-design/icons";
import { CommonContext } from '../../../../Context';

const SpendVisuals = () => {
	const [loader, setLoader] = useState(true);
	const [chartData, setChartData] = useState([]);
	const [apiData, setApiData] = useState([]);
	const [activeKey, setActiveKey] = useState(constants.applications);
	const [downloadLoading, setDownloadLoading] = useState(false);
	const [selectedDays, setSelectedDays] = useState(90);
	const secondLevelTabs = {
		DATE_RANGE: "1",
		THIS_MONTH: "2",
		SPEND_OVERTIME: "3",
		LAST_SELECTED_DAYS: "4",
	};
	const [secondLevelKey, setSecondLevelKey] = useState(secondLevelTabs.DATE_RANGE);
	const getDefaultDates = () => {
		const toDate = dayjs().format(constants.DateFormat2);
		const fromDate = dayjs().subtract(1, "month").format(constants.DateFormat2);
		return { fromDate, toDate };
	};
	const { fromDate: defaultFrom, toDate: defaultTo } = getDefaultDates();
	const [fromDate, setFromDate] = useState(defaultFrom);
	const [toDate, setToDate] = useState(defaultTo);
	const commonContext=useContext(CommonContext);
  const buyerId = commonContext.common.buyerId;

	const TAB_CONFIGSpendByDateRange = {
		[constants.applications]: {
			label: constants.Applications,
			apiRoute: WebLinks.GetMultiSpendVisualsApplications,
			chartType: "LineChart",
			options: () => lineOptions(),
			hasDateFilter: true,
		},
		[constants.applicationsByCostCenter]: {
			label: constants.ApplicationsByCostCenter,
			apiRoute: WebLinks.GetMultiSpendVisualsApplicationsByCostCenter,
			chartType: "LineChart",
			options: () => lineOptions(),
			hasDateFilter: true,
		},
		[constants.applicationsBySupplier]: {
			label: constants.ApplicationsBySupplier,
			apiRoute: WebLinks.GetMultiSpendVisualsApplicationsBySupplier,
			chartType: "LineChart",
			options: () => lineOptions(),
			hasDateFilter: true,
		},
		[constants.spend]: {
			label: constants.Spend,
			apiRoute: WebLinks.GetMultiSpendVisualsApplications,
			chartType: "ColumnChart",
			options: () => columnOptions("", "", "#43946C"),
			hasDateFilter: true,
		},
	};
	const TAB_CONFIGSpendThisMonth = {
		[constants.applicationsThisMonth]: {
			label: constants.ApplicationsThisMonth,
			apiRoute: WebLinks.GetMultiSpendVisualsAppsThisMonth,
			chartType: "ColumnChart",
			options: () => columnOptions("Day"),
		},
		[constants.appsThisMonthBySupplier]: {
			label: constants.ApplicationsThisMonthBySupplier,
			apiRoute: WebLinks.GetMultiSpendVisualsApplicationsThisMonthBySupplier,
			chartType: "LineChart",
			options: () => lineOptions(),
		},
		[constants.spendPerDayThisMonth]: {
			label: constants.SpendPerDayThisMonth,
			apiRoute: WebLinks.GetMultiSpendVisualsSpendThisMonth,
			chartType: "ColumnChart",
			options: () => columnOptions("Day", "£","#43946C"),
		},
		[constants.budgetVsSpendThisMonth]: {
			label: constants.BudgetVsSpendThisMonth,
			apiRoute: WebLinks.GetMultiSpendVisualsBudgetVsSpendThisMonth,
			chartType: "ColumnChart",
			options: () => columnOptions(),
		},
		[constants.spendOfBudgetSoFarThisMonth]: {
			label: constants.SpendOfBudgetSoFarThisMonth,
			apiRoute: WebLinks.GetMultiSpendVisualsBudgetSpendThisMonthBySupplier,
			chartType: "ColumnChart",
			options: () => columnOptions(),
		},
	};
	const TAB_CONFIGSpendOvertime = {
		[constants.applicationVolumeLast12Months]: {
			label: constants.ApplicationVolumeLast12Months,
			apiRoute: WebLinks.GetMultiSpendVisualsApplicationsLastMonths,
			chartType: "ColumnChart",
			options: () => columnOptions("Month"),
		},
		[constants.spendPerMonthLast24Months]: {
			label: constants.SpendPerMonthLast24Months,
			apiRoute: WebLinks.GetMultiSpendVisualsSpendLast24Months,
			chartType: "ColumnChart",
			options: () => columnOptions(),
		},
		[constants.cpabyOccupationLast90Days]: {
			label: constants.CpabyOccupationLast90Days,
			apiRoute: WebLinks.GetMultiSpendVisualsCPAbyOccupation,
			chartType: "ColumnChart",
			options: () => columnOptions(""),
		},
		[constants.liveJobsPerCostCenter]: {
			label: constants.JobPostingsByCostCenter,
			apiRoute: WebLinks.GetMultiSpendVisualsJobPostingsByCostCenter,
			chartType: "ColumnChart",
			options: () => columnOptions(""),
		},
	};
	const TAB_CONFIGLastSelectedDays = {
		[constants.applicationsByOccupation]: {
			label: constants.ApplicationsByOccupation,
			apiRoute: WebLinks.GetMultiSpendVisualsApplicationsByOccupation,
			chartType: "ColumnChart",
			options: () => columnOptions("", "", "#43946C"),
			hasDaysFilter: true,
		},
		[constants.totalApplicationByCostCenter]: {
			label: constants.TotalApplicationByCostCenter,
			header: constants.ApplicationsByCostCenter,
			apiRoute: WebLinks.GetMultiSpendVisualsTotalSpendByCostCenter,
			chartType: "ColumnChart",
			options: () => columnOptions(""),
			hasDaysFilter: true,
		},
		[constants.totalSpendByCostCenter]: {
			label: constants.TotalSpendByCostCenter,
			apiRoute: WebLinks.GetMultiSpendVisualsTotalSpendByCostCenter,
			chartType: "ColumnChart",
			options: () => columnOptions(""),
			hasDaysFilter: true,
		},
		[constants.expiredJobsInLastXDaysPerCostCenter]: {
			label: constants.ExpiredJobsInLastXDaysPerCostCenter,
			apiRoute: WebLinks.GetMultiSpendVisualsExpiredJobsLastDaysPerCostCenter,
			chartType: "ColumnChart",
			options: () => columnOptions(""),
			hasDaysFilter: true,
		},
	};

	const getTabConfig = () => {
		switch (secondLevelKey) {
			case secondLevelTabs.DATE_RANGE:
				return TAB_CONFIGSpendByDateRange;
			case secondLevelTabs.THIS_MONTH:
				return TAB_CONFIGSpendThisMonth;
			case secondLevelTabs.SPEND_OVERTIME:
				return TAB_CONFIGSpendOvertime;
			case secondLevelTabs.LAST_SELECTED_DAYS:
				return TAB_CONFIGLastSelectedDays;
			default:
				return {};
		}
	};
	const TAB_CONFIG = getTabConfig();
	const tabsList = Object.entries(TAB_CONFIG).map(([key, val]) => ({
		key,
		label: val.label,
		header: val.header,
	}));
	useEffect(() => {
		if (!buyerId || !activeKey) return;

		if (TAB_CONFIG[activeKey]?.hasDateFilter && (!fromDate || !toDate)) {
			return;
		}
		if (TAB_CONFIG[activeKey]?.hasDaysFilter && (!selectedDays)) {
			return;
		}
		getDashboardUrl();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeKey, buyerId, fromDate, toDate, secondLevelKey,selectedDays]);

	const lineOptions = (titleX = "", titleY = "") => ({
		titlePosition: "none",
		curveType: "none",
		chartArea: { top: 20, width: "80%", height: "80%" },
		legend: {
			position: "bottom",
			alignment: "center",
		},
		hAxis: {
			title: titleX,
			gridlines: {
				color: "#eee",
			},
			textPosition: "center",
			minValue: 0,
		},
		vAxis: {
			title: titleY,
			minValue: 0,
		},
		pointSize: 5,
		lineWidth: 2,
	});
	const columnOptions = (titleX = "", titleY = "", color="") => ({
		titlePosition: "none",
		chartArea: { top: 20, width: "80%", height: "80%" },
		legend: {
			position: "bottom",
			alignment: "center",
		},
		hAxis: {
			title: titleX,
			format: "0",
		},
		vAxis: {
			title: titleY,
			minValue: 0,
			format: "0",
		},
		colors: color ? [color] : undefined,
	});

	const formattedText = (apiData) => {
		let formattedData = [];
		switch (activeKey) {
			case constants.applications:
				formattedData = [
					["Date", "Total Apps", "Quality Fail", "Interview", "Hired"],
					...apiData.map(item => [
						item.date,
						item.totalApps ?? 0,
						item.qualityFail ?? 0,
						item.interview ?? 0,
						item.hired ?? 0,
					])
				];
				break;
			case constants.applicationsByCostCenter: {
				const keys = [...new Set(apiData.map(i => i?.reportName))];
				const indexes = [...new Set(apiData.map(i => i?.date))];
				const map = {};
				apiData.forEach(i => {
					if (!map[i.date]) map[i.date] = {};
					map[i.date][i.reportName] = i.totalApps ?? 0;
				});
				formattedData = [
					["Date", ...keys],
					...indexes.map(date => [
						date,
						...keys.map(k => map[date]?.[k] ?? 0)
					])
				];
				break;
			}
			case constants.applicationsBySupplier: {
				const keys = [...new Set(apiData.map(i => i?.displayName))];
				const indexes = [...new Set(apiData.map(i => i?.date))];
				const map = {};
				apiData.forEach(i => {
					if (!map[i.date]) map[i.date] = {};
					map[i.date][i.displayName] = i.totalApps ?? 0;
				});
				formattedData = [
					["Date", ...keys],
					...indexes.map(date => [
						date,
						...keys.map(k => map[date]?.[k] ?? 0)
					])
				];
				break;
			}
			case constants.spend:
				formattedData = [
					["Days", "Cost"],
					...apiData.map(item => [
						item?.date,
						item.cost ? parseFloat(item.cost.toFixed(2)) : 0,
					])
				];
				break;
			case constants.applicationsThisMonth:
				formattedData = [
					["Days", "Total Apps", "Quality Fail", "Interview", "Hired"],
					...apiData.map(item => [
						item?.days?.toString(),
						item.totalApps ?? 0,
						item.qualityFail ?? 0,
						item.interview ?? 0,
						item.hired ?? 0,
					])
				];
				break;
			case constants.appsThisMonthBySupplier: {
				const keys = [...new Set(apiData.map(i => i?.name))];
				const indexes = [...new Set(apiData.map(i => i?.days))].sort((a, b) => a - b);
				const map = {};
				apiData.forEach(i => {
					const day = String(i.days);
					if (!map[day]) map[day] = {};
					map[day][i.name] = i.totalApps ?? 0;
				});
				formattedData = [
					["Day", ...keys],
					...indexes.map(d => {
						const day = String(d);
						return [
							day,
							...keys.map(k => map[day]?.[k] ?? 0)
						];
					})
				];
				break;
			}
			case constants.spendPerDayThisMonth:
				formattedData = [
					["Days", "Cost"],
					...apiData.map(item => [
						item?.days,
						item.cost ? parseFloat(item.cost.toFixed(2)) : 0,
					])
				];
				break;
			case constants.applicationVolumeLast12Months: {
				const sorted = [...apiData].sort((a, b) => a.yyyyMM - b.yyyyMM);
				formattedData = [
					["Month", "Total Apps", "Quality Fail", "Interview", "Hired"],
					...sorted.map(item => [
						item.yyyyMM.toString(),
						item.totalApps ?? 0,
						item.qualityFail ?? 0,
						item.interview ?? 0,
						item.hired ?? 0,
					])
				];
				break;
			}
			case constants.spendPerMonthLast24Months: {
				const years = [...new Set(apiData.map(i => i.year))].sort();
				const months = [...new Set(apiData.map(i => i.month))].sort((a, b) => a - b);

				const map = {};
				apiData.forEach(i => {
					if (!map[i.month]) map[i.month] = {};
					map[i.month][i.year] = i.spend ?? 0;
				});

				formattedData = [
					["Month", ...years.map(String)],
					...months.map(m => [
						m.toString(),
						...years.map(y => map[m]?.[y] ?? 0)
					])
				];
				break;
			}
			case constants.cpabyOccupationLast90Days:
				formattedData = [
					["Month", "CPA"],
					...apiData.map((item) => [
						item?.occupation,
						item.cpa ? parseFloat(item.cpa.toFixed(2)) : 0,
					]),
				];
				break;
			case constants.liveJobsPerCostCenter:
				formattedData = [
					["Name", "Job Count"],
					...apiData.map(item => [
						item?.reportName,
						item?.jobCount ?? 0
					])
				];
				break;
			case constants.applicationsByOccupation:
				formattedData = [
					["Occupation", "Apps"],
					...apiData.map(item => [
						item?.occupation,
						item?.apps ?? 0
					])
				];
				break;
			case constants.budgetVsSpendThisMonth:
				formattedData = [
					["Month", "Spend", "Budget"],
					[
						"This Month",
						apiData?.spend ? parseInt(apiData.spend) : 0,
						apiData?.budget ? parseInt(apiData.budget) : 0
					]
				];
				break;
			case constants.spendOfBudgetSoFarThisMonth:
				formattedData = [
					["Supplier", "Percentage Spend"],
					...apiData.map(item => [
						item?.name || "-",
						item?.percentagespend ?? 0
					])
				];
				break;
			case constants.totalApplicationByCostCenter:
				formattedData = [
					["Name", "Total Apps"],
					...apiData.map(item => [
						item?.reportName,
						item.totalApps ?? 0,
					])
				];
				break;
			case constants.totalSpendByCostCenter:
				formattedData = [
					["Name","Spend"],
					...apiData.map(item => [
						item?.reportName,
						item.spend ? parseFloat(item.spend.toFixed(2)) : 0,
					])
				];
				break;
			case constants.expiredJobsInLastXDaysPerCostCenter:
				formattedData = [
					["Name", "Job Count"],
					...apiData.map(item => [
						item?.reportName,
						item.jobCount ?? 0,
					])
				];
				break;
			default:
				formattedData = [];
		}
		return formattedData;
	};
	const buildDashboardUrl = (activeKey) => {
		let params = `buyerId=${buyerId}`;
		if (TAB_CONFIG[activeKey]?.hasDateFilter) {
			params += `&startDate=${fromDate}&endDate=${toDate}`;
		}
		if (TAB_CONFIG[activeKey]?.hasDaysFilter) {
			params += `&days=${selectedDays}`;
		}
		if (activeKey === constants.applicationVolumeLast12Months || activeKey === constants.jobsPostedPerMonth) {
			params += `&months=${12}`
		}
		return `${TAB_CONFIG[activeKey]?.apiRoute}?${params}`;
	};

	const getDashboardUrl = async () => {
		setLoader(true);
		try {
			const url = buildDashboardUrl(activeKey);
			const response = await APIUtils.apiCall('GET', url);
			if (!response.isCatch && response.resultObj.statusCode === "2000") {
				const apiData = response.resultObj.data || [];
				setChartData(formattedText(apiData));
				setApiData(apiData);
			} else {
				message.error(response?.resultObj?.message || "Something went wrong");
			}
		} catch (error) {
			console.error("API Error:", error);
			message.error("Failed to fetch dashboard data");
		} finally {
			setLoader(false);
		}
	};

	const handleDateChange = (type, date, dateString) => {
		if (type === "from") {
			if (toDate && dayjs(dateString).isAfter(dayjs(toDate))) {
				message.error("From date cannot be after To date");
				return;
			}
			setFromDate(dateString);
		}

		if (type === "to") {
			if (fromDate && dayjs(dateString).isBefore(dayjs(fromDate))) {
				message.error("To date cannot be before From date");
				return;
			}
			setToDate(dateString);
		}
	};

	const disableDate = (type, current) => {
		if (!current || current > dayjs().endOf("day")) return true;

		const compareDate = type === "from" ? toDate : fromDate;
		if (!compareDate) return false;

		return type === "from"
			? current > dayjs(compareDate)
			: current < dayjs(compareDate);
	};

	const handleChangeActiveKey = (e) => {
		setActiveKey(e)
	};

	const getChart = (type, options) => (
		loader ? (
			<Spin className='min-h-[50vh] flex justify-center items-center' />
		) : !hasData ? (
			<Empty className='mt-5' description={constants.NO_DATA} />
		) : (
			<Chart
				key={activeKey}
				chartType={type}
				height="600px"
				data={chartData}
				options={options}
				loader={<Spin className='min-h-[50vh] flex justify-center items-center' />}
			/>
		)
	)
	const hasData = chartData ? chartData?.length > 1 : false;
	const downloadFile = (tabName) => {
		try {
			if (!apiData || apiData.length === 0) {
				message.error("No data to download");
				return;
			}
			setDownloadLoading(true);
			let excelData = [];
			switch (activeKey) {
				case constants.applications:
					excelData = apiData.map(item => ({
						"Date": item.date,
						"Total Apps": item.totalApps ?? 0,
						"Quality Fail": item.qualityFail ?? 0,
						"Interview": item.interview ?? 0,
						"Hired": item.hired ?? 0,
						"Cost": item.cost ? parseFloat(item.cost.toFixed(2)) : 0,
					}));
					break;
				case constants.applicationsByCostCenter:
					excelData = apiData.map(item => ({
						"Date": item?.date,
						"Name": item?.reportName,
						"TotalApps": item?.totalApps ?? 0,
					}));
					break;
				case constants.applicationsBySupplier:
					excelData = apiData.map(item => ({
						"Date": item?.date,
						"Name": item?.displayName,
						"TotalApps": item?.totalApps ?? 0,
					}));
					break;
				case constants.spend:
					excelData = apiData.map(item => ({
						"Date": item?.date,
						"Total Apps": item.totalApps ?? 0,
						"Quality Fail": item.qualityFail ?? 0,
						"Interview": item.interview ?? 0,
						"Hired": item.hired ?? 0,
						"Cost": item.cost ? parseFloat(item.cost.toFixed(2)) : 0,
					}));
					break;
				case constants.applicationsThisMonth:
					excelData = apiData.map(item => ({
						"Day": item?.days,
						"Total Apps": item.totalApps ?? 0,
						"Quality Fail": item.qualityFail ?? 0,
						"Interview": item.interview ?? 0,
						"Hired": item.hired ?? 0,
						"Cost": item.cost ? parseFloat(item.cost.toFixed(2)) : 0,
					}));
					break;
				case constants.appsThisMonthBySupplier:
					excelData = apiData.map(item => ({
						"Day": item?.days,
						"Name": item?.name,
						"LogoURL": item?.logoURL,
						"TotalApps": item?.totalApps ?? 0,
						"Quality Fail": item.qualityFail ?? 0,
						"Interview": item.interview ?? 0,
						"Hired": item.hired ?? 0,
						"Cost": item.cost ? parseFloat(item.cost.toFixed(2)) : 0,
					}));
					break;
				case constants.spendPerDayThisMonth:
					excelData = apiData.map(item => ({
						"Day": item?.days,
						"TotalApps": item?.totalApps ?? 0,
						"Cost": item.cost ? parseFloat(item.cost.toFixed(2)) : 0,
					}));
					break;
				case constants.budgetVsSpendThisMonth:
					excelData = [{
						"Spend": apiData?.spend ? parseInt(apiData?.spend) : 0,
						"Budget": apiData?.budget ? parseInt(apiData?.budget) : 0
					}];
					break;
				case constants.spendOfBudgetSoFarThisMonth:
					excelData = apiData.map(item => ({
						"Supplier": item?.name || "-",
						"Budget": item?.budget ? parseInt(item?.budget) : 0,
						"PercentageSpend": item?.percentagespend ?? 0,
					}));
					break;
				case constants.applicationVolumeLast12Months:
					excelData = apiData.map(item => ({
						"Year": item.yyyyMM,
						"Month": item.month,
						"Total Apps": item.totalApps ?? 0,
						"Quality Fail": item.qualityFail ?? 0,
						"Interview": item.interview ?? 0,
						"Hired": item.hired ?? 0,
						"Cost": item.cost ? parseFloat(item.cost.toFixed(2)) : 0
					}));
					break;
				case constants.spendPerMonthLast24Months:
					excelData = apiData.map(item => ({
						"Month": item?.month,
						"Year": item?.year,
						"Spend": item.cost ? parseFloat(item.cost.toFixed(2)) : 0,
					}));
					break;
				case constants.cpabyOccupationLast90Days:
					excelData = apiData.map(item => ({
						"Occupation": item?.occupation,
						"CPA": item?.cpa ?? 0,
						"Apps": item?.apps?? 0,
					}));
					break;
				case constants.liveJobsPerCostCenter:
					excelData = apiData.map(item => ({
						"Name": item?.reportName,
						"Job Count": item?.jobCount ?? 0,
					}));
					break;
				case constants.applicationsByOccupation:
					excelData = apiData.map(item => ({
						"Occupation": item?.occupation,
						"Apps": item?.apps ?? 0,
					}));
					break;
				case constants.totalApplicationByCostCenter:
					excelData = apiData.map(item => ({
						"Name": item?.reportName,
						"Total Apps": item?.totalApps ?? 0,
						"Spend": item?.spend ?? 0,
					}));
					break;
				case constants.totalSpendByCostCenter:
					excelData = apiData.map(item => ({
						"Name": item?.reportName,
						"Total Apps": item?.totalApps ?? 0,
						"Spend": item?.spend ?? 0,
					}));
					break;
				case constants.expiredJobsInLastXDaysPerCostCenter:
					excelData = apiData.map(item => ({
						"Name": item?.reportName,
						"Job Count": item?.jobCount ?? 0,
					}));
					break;
				default:
					message.warning("Download failed");
					setDownloadLoading(false);
					return;
			}
			const worksheet = XLSX.utils.json_to_sheet(excelData);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
			const fileName = TAB_CONFIG[activeKey]?.hasDateFilter
				? `${tabName} ${fromDate} - ${toDate}.xlsx`
				: `${tabName}.xlsx`;
			setTimeout(() => {
				XLSX.writeFile(workbook, fileName);
				setDownloadLoading(false);
			}, 300);
		} catch (err) {
			setDownloadLoading(false);
			message.error("Error downloading file");
		}
	};
	const renderHeader = (tabName) => (
		<div
			className={`justify-end flex flex-wrap`}
		>
			<Button
				className='bg-green-800 text-white hover:!text-white hover:!border-green-500'
				icon={<FileExcelOutlined />}
				onClick={() => downloadFile(tabName)}
				loading={downloadLoading}
			>
				Download as Excel
			</Button>
		</div>
	);
	const displayDate = () => {
		return (
			<Form>
				<Space>
					<Form.Item label="From" className="mb-3">
						<DatePicker
							onChange={(date, dateString) => handleDateChange("from", date, dateString)}
							value={fromDate ? dayjs(fromDate) : null}
							disabledDate={(current) => disableDate("from", current)}
							allowClear={false}
						/>
					</Form.Item>
					<Form.Item label="To" className="mb-3">
						<DatePicker
							onChange={(date, dateString) => handleDateChange("to", date, dateString)}
							value={toDate ? dayjs(toDate) : null}
							disabledDate={(current) => disableDate("to", current)}
							allowClear={false}
						/>
					</Form.Item>
				</Space>
			</Form>
		);
	};
	const displayDays = () => (
		<div className="flex items-center gap-2 w-[70%] lg:w-48">
			<span className="text-sm whitespace-nowrap">
				Jobs Posted in Last:
			</span>
			<Select
				value={selectedDays}
				onChange={setSelectedDays}
				options={constants.DaysExtended}
				className="flex-1 lg:w-48"
			/>
		</div>
	)
	const secondLevelTabItems = [
		{
			key: secondLevelTabs.DATE_RANGE,
			label: constants.AppsSpendByDateRange,
		},
		{
			key: secondLevelTabs.THIS_MONTH,
			label: constants.AppsSpendThisMonth,
		},
		{
			key: secondLevelTabs.SPEND_OVERTIME,
			label: constants.JobsAppsSpendLast12Months,
		},
		{
			key: secondLevelTabs.LAST_SELECTED_DAYS,
			label: "Apps/Spend in Last Selected Days",
		},
	];
	return (
		<div>
			<Tabs
				activeKey={secondLevelKey}
				onChange={(key) => {
					setSecondLevelKey(key);
					const firstTab = Object.keys(
						key === secondLevelTabs.DATE_RANGE
							? TAB_CONFIGSpendByDateRange
							: key === secondLevelTabs.THIS_MONTH
								? TAB_CONFIGSpendThisMonth
								: key === secondLevelTabs.SPEND_OVERTIME
									? TAB_CONFIGSpendOvertime
									: key === secondLevelTabs.LAST_SELECTED_DAYS
										? TAB_CONFIGLastSelectedDays
										: ""
					)[0];
					setActiveKey(firstTab);
				}}
				items={secondLevelTabItems}
			/>
			{secondLevelKey === secondLevelTabs.DATE_RANGE && displayDate()}
			{TAB_CONFIG[activeKey]?.hasDaysFilter && displayDays()}
			<Tabs
				activeKey={activeKey}
				onChange={handleChangeActiveKey}
				items={tabsList.map(({ key, label, header }) => {
					const config = TAB_CONFIG[key];
					return {
						key,
						label: header ? header :label,
						children: (
							<>
								{renderHeader(label)}
								<div className="text-center my-3 text-xl">
									{label} {config?.hasDateFilter ? ` - ${fromDate} to ${toDate}` : ""}
								</div>
								{getChart(config.chartType, config.options())}
							</>
						),
					};
				})}
			/>
		</div>
	);
};
export default SpendVisuals;
